import { Controller } from "@hotwired/stimulus"

// Inline edycja daty wykonania w boxie zadań na karcie klienta.
//
// Zasada: wybieranie w kalendarzu (dzień, godzina, minuty) NIE robi nic - ani nie wysyła
// requestu, ani nie rusza plakietki. Zapis idzie raz, przy odkliknięciu w tło; wtedy znika
// ołówek, a turbo_stream odświeża plakietkę. Dzięki temu zachowanie jest spójne dla dnia
// i godziny, a żaden request nie podmienia ramki spod otwartego kalendarza.
//
// Wyjątek: konto bez godzin. Flatpickr zamyka wtedy kalendarz sam po wybraniu dnia,
// więc wybór dnia jest już zakończoną decyzją i zapisujemy od razu (bez czekania na odklik).
export default class extends Controller {
  static targets = ["input", "wrapper", "text"]
  static values = { withTime: Boolean }

  connect() {
    // punkt odniesienia: wartość, z jaką pole przyszło z serwera
    this.valueBefore = this.inputTarget.value
  }

  disconnect() {
    this.stopWatchingOutsideClick()
  }

  // Klik w plakietkę: ołówek zostaje widoczny na czas edycji, a od tej chwili pilnujemy
  // kliknięcia w tło. Nasłuch wisi na document, NIE na zdarzeniach flatpickra - to dlatego
  // po zapisie i podmianie ramce nie ma czego zapętlić.
  startEdit() {
    if (this.hasWrapperTarget) { this.wrapperTarget.dataset.editing = "" }

    this.outsideHandler = this.outsideHandler || this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.outsideHandler)

    // Godzinę/minutę wpisaną z klawiatury musimy złapać JUŻ PRZY PISANIU: przy zamykaniu
    // kalendarza flatpickr przepisuje te pola ze swojego stanu, czyli kasuje wpisaną wartość,
    // zanim zdążymy ją odczytać (potwierdzone testem systemowym: "27" -> "20").
    this.timeInputHandler = this.timeInputHandler || this.handleTimeInput.bind(this)
    document.addEventListener("input", this.timeInputHandler, true)
  }

  endEdit() {
    this.stopWatchingOutsideClick()
    if (this.hasWrapperTarget) { delete this.wrapperTarget.dataset.editing }
  }

  // Zapamiętuje to, co user wpisuje w pola godziny i minut kalendarza
  handleTimeInput(event) {
    const classes = event.target?.classList
    if (!classes) { return }

    if (classes.contains("flatpickr-hour")) { this.typedHour = event.target.value }
    if (classes.contains("flatpickr-minute")) { this.typedMinute = event.target.value }
  }

  // Zmiana w kalendarzu. Przy koncie z godzinami nie robimy NIC - decyzja jest zakończona
  // dopiero przy odkliknięciu (inaczej dzień zapisywałby się w środku ustawiania godziny).
  // Bez godzin zapisujemy od razu, bo flatpickr właśnie sam zamknął kalendarz.
  changed() {
    if (!this.withTimeValue) { this.commit() }
  }

  // Kalendarz flatpickra żyje w document.body, czyli poza tym formularzem - bez tego wyjątku
  // klik w dzień liczyłby się jako kliknięcie w tło i zapisywał w środku wybierania.
  handleOutsideClick(event) {
    if (this.element.contains(event.target)) { return }
    if (event.target?.closest?.(".flatpickr-calendar")) { return }

    this.commit()
  }

  commit() {
    this.endEdit()

    // pole wyczyszczone przez usera - postpone odrzuciłby to błędem, więc nie zapisujemy
    // (sprawdzamy przed currentValue, bo picker trzyma jeszcze poprzednią datę)
    if (!this.inputTarget.value) { return }

    const date = this.currentDate()
    const value = this.currentValue()
    if (!value || value === this.valueBefore) { return } // bez zmiany
    if (this.submitting) { return }

    this.submitting = true
    this.showSaved(date)
    this.typedHour = null
    this.typedMinute = null
    // wpisujemy wprost do pola, bo to ono leci w formularzu
    this.inputTarget.value = value
    this.inputTarget._flatpickr?.close()
    // flatpickr otwiera się na focusie, więc bez blur() turbo przywróciłoby focus po
    // podmianie ramki i kalendarz wyskoczyłby z powrotem
    this.inputTarget.blur()

    if (this.element.requestSubmit) { this.element.requestSubmit() } else { this.element.submit() }
  }

  // Aktualnie wybrana data. Godzinę i minutę bierzemy z tego, co user WPISAŁ (typedHour/
  // typedMinute z nasłuchu przy pisaniu), bo flatpickr przy zamykaniu kalendarza przywraca
  // te pola ze swojego stanu i kasuje wpisaną wartość, zanim tu dojdziemy.
  currentDate() {
    const picker = this.inputTarget._flatpickr
    const selected = picker?.selectedDates?.[0]
    if (!picker || !selected) { return null }

    const date = new Date(selected)
    if (picker.hourElement && picker.minuteElement) {
      // typedHour/typedMinute mają pierwszeństwo - pola pickera mogły zostać przywrócone
      // do starej wartości przy zamykaniu kalendarza
      const hour = this.typedHour ?? picker.hourElement.value
      const minute = this.typedMinute ?? picker.minuteElement.value
      date.setHours(parseInt(hour, 10) || 0, parseInt(minute, 10) || 0, 0, 0)
    }

    return date
  }

  currentValue() {
    const picker = this.inputTarget._flatpickr
    const date = this.currentDate()
    if (!date) { return this.inputTarget.value }

    return picker.formatDate(date, picker.config.dateFormat)
  }

  // Napis na plakietce od razu pokazuje zapisywaną datę. Bez tego po odkliknięciu widać
  // jeszcze starą wartość, aż turbo_stream podmieni ramkę - czyli krótkie, bezsensowne mignięcie.
  showSaved(date) {
    const picker = this.inputTarget._flatpickr
    if (!this.hasTextTarget || !picker || !date) { return }

    this.textTarget.textContent = picker.formatDate(date, "Y-m-d")
  }

  stopWatchingOutsideClick() {
    if (this.outsideHandler) { document.removeEventListener("click", this.outsideHandler) }
    if (this.timeInputHandler) { document.removeEventListener("input", this.timeInputHandler, true) }
  }
}
